// WS4 (ARCA-84) — shared SVG icon set.
//
// Ports the take-two folk-art line-icon library
// (Docs/UI-prototype take two/project/src/icons.jsx — the `ICON` map +
// `FolkStar` + `PieceGlyph`) into a single reusable component, replacing the
// emoji-as-icons (🥇 ✉ ✓ …) and the per-consumer inline-SVG duplicates
// (Screen.tsx `BackChevron`, etc.) scattered across the app. The shared set was
// stubbed-for in WS1/WS2 (see the TODO anchors in Screen.tsx / pieces.css) but
// never built; WS4 lands it.
//
// Conventions mirror the sibling PowerIcon primitive: 24×24 viewBox,
// stroke=currentColor, round caps/joins, strokeWidth 2. Icons are DECORATIVE by
// default (aria-hidden) because the overwhelming majority sit beside a text
// label; pass `label` to promote a standalone icon to role="img".

import type { CSSProperties, JSX } from 'react'

// ---------- line-icon path library (verbatim from take-two icons.jsx) ----------
const ICON = {
  back: <path d="M15 5l-7 7 7 7" />,
  chevron: <path d="M9 6l6 6-6 6" />,
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="M13 6l6 6-6 6" />
    </>
  ),
  play: <path d="M8 5.5l11 6.5-11 6.5z" fill="currentColor" stroke="none" />,
  arrowUp: (
    <>
      <path d="M12 19V6" />
      <path d="M6 12l6-6 6 6" />
    </>
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  check: <path d="M5 13l4 4 10-11" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.6M12 18.6v2.6M21.2 12h-2.6M5.4 12H2.8M18.5 5.5l-1.8 1.8M7.3 16.7l-1.8 1.8M18.5 18.5l-1.8-1.8M7.3 7.3L5.5 5.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.3-4.3" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="2.5" />
      <path d="M5 15V6a2 2 0 012-2h9" />
    </>
  ),
  share: (
    <>
      <path d="M12 15V4" />
      <path d="M8 8l4-4 4 4" />
      <path d="M5 13v5a2 2 0 002 2h10a2 2 0 002-2v-5" />
    </>
  ),
  message: (
    <path d="M5 5h14a1.5 1.5 0 011.5 1.5v8A1.5 1.5 0 0119 16H9l-4 3.5V6.5A1.5 1.5 0 015 5z" />
  ),
  bell: (
    <>
      <path d="M6.5 16V11a5.5 5.5 0 1111 0v5l1.5 2H5z" />
      <path d="M10 20a2 2 0 004 0" />
    </>
  ),
  flip: (
    <>
      <path d="M4 9a8 8 0 0114-3M20 15A8 8 0 016 18" />
      <path d="M18 3v4h-4M6 21v-4h4" />
    </>
  ),
  swords: (
    <>
      <path d="M14.5 4H20v5.5L9 20.5 3.5 15z" />
      <path d="M9.5 4H4v5.5L15 20.5 20.5 15" />
      <path d="M11 13l2 2" />
    </>
  ),
  pencil: (
    <>
      <path d="M4 20l1-4L16 5l3 3L8 19z" />
      <path d="M14 7l3 3" />
    </>
  ),
  flame: (
    <path d="M12 3c2.5 3 4 5 4 8a4 4 0 11-8 0c0-1.2.5-2.2 1.3-3-.2 1.4.7 2.3 1.4 2.3.9 0 1.3-.8 1-1.8-.5-1.7.3-3.7.3-5.5z" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16 7.5a3 3 0 010 5" />
      <path d="M17.5 14c2 .5 3.5 2.3 3.5 5" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4l8.5 15H3.5z" />
      <path d="M12 10v4" />
      <path d="M12 16.5v.2" />
    </>
  ),
  question: (
    <>
      <path d="M9 9.5a3 3 0 115.5 1.6c-1 1.1-2.5 1.4-2.5 3" />
      <path d="M12 17.5v.2" />
    </>
  ),
  scales: (
    <>
      <path d="M12 4v16M7 20h10" />
      <path d="M12 6L5 8m7-2l7 2" />
      <path d="M5 8l-2.2 4.5a2.3 2.3 0 004.4 0zm14 0l-2.2 4.5a2.3 2.3 0 004.4 0z" />
    </>
  ),
  scroll: (
    <>
      <path d="M7 5h10v12a2 2 0 002 2H8a2 2 0 01-2-2V6" />
      <path d="M17 5a2 2 0 012 2v1h-2" />
      <path d="M9 9h6M9 12h6" />
    </>
  ),
  gem: (
    <>
      <path d="M7 4h10l4 5-9 11L3 9z" />
      <path d="M3 9h18M9 4l-2 5 5 11 5-11-2-5" />
    </>
  ),
  trophy: (
    <>
      <path d="M8 4h8v5a4 4 0 01-8 0z" />
      <path d="M8 6H5v1a3 3 0 003 3M16 6h3v1a3 3 0 01-3 3" />
      <path d="M12 13v3M9 20h6M10 16h4l1 4H9z" />
    </>
  ),
  medal: (
    <>
      <circle cx="12" cy="14" r="5" />
      <path d="M9 4l3 5 3-5" />
      <path d="M12 12l.8 1.7 1.9.2-1.4 1.3.4 1.8L12 16.4l-1.7.9.4-1.8-1.4-1.3 1.9-.2z" />
    </>
  ),
  smiley: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="9.3" cy="10.5" r=".9" fill="currentColor" stroke="none" />
      <circle cx="14.7" cy="10.5" r=".9" fill="currentColor" stroke="none" />
      <path d="M8.5 14.5c1 1.3 2.2 2 3.5 2s2.5-.7 3.5-2" />
    </>
  ),
  sprout: (
    <>
      <path d="M12 21V10" />
      <path d="M12 13c-3 0-5-1.6-5-5 3 0 5 1.6 5 5zM12 11c3-.7 4.6-3 4.6-6C13.6 5 12 7.3 12 11z" />
    </>
  ),
} as const

export type IconName = keyof typeof ICON

export interface IconProps {
  name: IconName
  /** SVG width/height in px. Default 20 (take-two default). */
  size?: number
  /** Stroke width. Default 2. */
  sw?: number
  /** Stroke color. Default currentColor (inherits text color). */
  color?: string
  /** Fill. Default none (line icons). */
  fill?: string
  /**
   * Accessible label. When set, the icon is promoted to role="img" with this
   * label. Omit for decorative icons sitting beside text (default aria-hidden).
   */
  label?: string
  style?: CSSProperties
  className?: string
}

/**
 * Folk-art line icon. Decorative by default (aria-hidden); pass `label` to make
 * it a standalone role="img". Unknown names fall back to `question` (mirrors the
 * take-two prototype's `ICON[name] || ICON.question`).
 */
export function Icon({
  name,
  size = 20,
  sw = 2,
  color = 'currentColor',
  fill = 'none',
  label,
  style,
  className,
}: IconProps): JSX.Element {
  const body = ICON[name] ?? ICON.question
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      className={className}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      xmlns="http://www.w3.org/2000/svg"
    >
      {label ? <title>{label}</title> : null}
      {body}
    </svg>
  )
}

// ---------- folk star (replaces the ✦ / ★ glyph used as a token) ----------
export interface FolkStarProps {
  size?: number
  color?: string
  glow?: boolean
  style?: CSSProperties
  className?: string
}

export function FolkStar({
  size = 24,
  color = 'var(--color-on-action)',
  glow,
  style,
  className,
}: FolkStarProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      style={
        glow
          ? { filter: 'drop-shadow(0 0 3px rgba(255,210,120,.9))', ...style }
          : style
      }
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 4c.6 5 2 6.4 7 7-5 .6-6.4 2-7 7-.6-5-2-6.4-7-7 5-.6 6.4-2 7-7z"
        fill={color}
      />
    </svg>
  )
}

// ---------- enamel chess pieces (white silhouette on a colored disc) ----------
const PIECE_SVG = {
  folk: <circle cx="24" cy="24" r="9" />,
  king: <path d="M24 5v6M21 8h6M16 38c0-9 4-13 8-13s8 4 8 13zM18 38h12" />,
  queen: <path d="M14 17l3 16h14l3-16-5 7-5-9-5 9zM17 36h14" />,
  rook: <path d="M15 13h3v3h4v-3h4v3h3v5l-2 2v9H17v-9l-2-2zM16 38h16" />,
  bishop: (
    <path d="M24 6c4 3 6 7 6 11 0 5-3 9-6 12-3-3-6-7-6-12 0-4 2-8 6-11zM18 36h12M24 14v6M21 17h6" />
  ),
  knight: (
    <path d="M16 38c0-6 1-10 5-13l-3 1-1-3 4-3c2-4 6-5 9-3 3 2 4 6 4 12 0 5-1 9-3 12zM21 18l-2 2" />
  ),
} as const

export type PieceType = keyof typeof PIECE_SVG

export interface PieceGlyphProps {
  type?: PieceType
  size?: number
  color?: string
  label?: string
}

export function PieceGlyph({
  type = 'folk',
  size = 30,
  color = 'var(--color-on-action)',
  label,
}: PieceGlyphProps): JSX.Element {
  const filled = type === 'folk'
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill={filled ? color : 'none'}
      stroke={color}
      strokeWidth={type === 'knight' ? 2.6 : 2.8}
      strokeLinejoin="round"
      strokeLinecap="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      xmlns="http://www.w3.org/2000/svg"
    >
      {label ? <title>{label}</title> : null}
      {PIECE_SVG[type] ?? PIECE_SVG.folk}
    </svg>
  )
}
